import { Box, Button, TextField, Typography, useTheme, Alert, CircularProgress } from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import { tokens } from "../../theme";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { login } from "../../services/auth";
import { useBrand } from "../../contexts/BrandContext";

const LoginPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { initializeBrands } = useBrand();

  const handleFormSubmit = async (values) => {
    setError('');
    setLoading(true);

    try {
      // Step 1: Authenticate user
      await login(values.username, values.password);
      
      // Step 2: Initialize brands after successful login
      console.log('[LoginPage] Login successful, initializing brands...');
      await initializeBrands();
      console.log('[LoginPage] Brands initialized, navigating to dashboard...');
      
      // Step 3: Navigate after brands are loaded
      navigate("/dashboard");
    } catch (err) {
      setError(err.message || 'Échec de la connexion. Veuillez vérifier vos identifiants.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      bgcolor="#f8f9fa" // fond général très clair
    >
      <Box
        bgcolor="#fff" // bloc central blanc
        p="2.5rem"
        borderRadius="16px"
        width="100%"
        maxWidth="400px"
        boxShadow="0 6px 32px 0 rgba(25, 118, 210, 0.07), 0 1.5px 8px 0 rgba(0,0,0,0.03)"
      >
        <Typography variant="h3" color="#1976d2" fontWeight={700} mb="2rem" textAlign="center" fontFamily="Montserrat, Arial, sans-serif">
          Connexion
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Formik
          initialValues={{
            username: "",
            password: "",
          }}
          validationSchema={yup.object().shape({
            username: yup.string().required("Nom d'utilisateur requis"),
            password: yup.string().required("Mot de passe requis"),
          })}
          onSubmit={handleFormSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            handleChange,
            handleSubmit,
          }) => (
            <form onSubmit={(e) => {
              e.preventDefault();
              handleSubmit(e);
            }}>
              <Box display="flex" flexDirection="column" gap="1.5rem">
                <TextField
                  fullWidth
                  variant="outlined"
                  type="text"
                  label="Nom d'utilisateur"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.username}
                  name="username"
                  error={!!touched.username && !!errors.username}
                  helperText={touched.username && errors.username}
                  InputProps={{
                    style: { background: '#fff', borderRadius: 8, fontFamily: 'Montserrat, Arial, sans-serif' }
                  }}
                />
                <TextField
                  fullWidth
                  variant="outlined"
                  type="password"
                  label="Mot de passe"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.password}
                  name="password"
                  error={!!touched.password && !!errors.password}
                  helperText={touched.password && errors.password}
                  InputProps={{
                    style: { background: '#fff', borderRadius: 8, fontFamily: 'Montserrat, Arial, sans-serif' }
                  }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  sx={{
                    bgcolor: '#1976d2',
                    color: '#fff',
                    fontWeight: 700,
                    fontFamily: 'Montserrat, Arial, sans-serif',
                    borderRadius: 2,
                    fontSize: 16,
                    py: 1.2,
                    mt: 1,
                    '&:hover': { bgcolor: '#145ea8' }
                  }}
                >
                  {loading ? <CircularProgress size={24} sx={{ color: 'inherit' }} /> : 'Se connecter'}
                </Button>
                
                
                <Button
                  href="/forgot-password"
                  sx={{
                    color: '#1976d2',
                    textTransform: 'none',
                    fontSize: 14,
                    mt: -1,
                    mb: 1,
                    alignSelf: 'flex-end',
                  }}
                >
                  Mot de passe oublié ?
                </Button>
              </Box>
            </form>
          )}
        </Formik>
      </Box>
    </Box>
  );
};

export default LoginPage;
